import React, { useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Close } from '@mui/icons-material';
import AttachIcon from '@mui/icons-material/AttachFile';
import { map as asyncMap } from 'p-iteration';
import { uniqueId } from 'lodash';
import filesize from "filesize";
import { makeStyles, withStyles } from '@mui/styles';
import {
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    FormControl,
    TextField,
} from "@mui/material";
import Upload from '../../UI/Upload';
import FileList from '../../UI/FileList';
import { i18n } from '../../translate/i18n';
import processUpload from '../../utils/processUpload';
import resizeFile from '../../utils/imageResizer';
import * as actions from "../../store/actions";

const styles = {
	modalStyle: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	avatar: {
		width: 60,
		height: 60,
		fontSize: 13
	},
	sendButton: {
		backgroundColor: 'green',
		color: '#fff',
		margin: '0.75rem 1rem'
	},
	closeButton: {
		position: 'absolute',
		fontSize: '1.1rem',
		top: 10,
		right: 10,
	},
	uploadFileBox: {
		width: '100%',
	},
  fileError: {
    color: '#f44336'
  },
  sectionTitleContainer: {
    display: 'flex',
    marginTop: 20,
  },
};

const useStyles = makeStyles(() => ({
	paper: { minWidth: "50vw" },
	root: {
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "grey"
    },
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "grey"
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "grey",
			borderWidth: '1px'
    },
  }
}));


const DialogTitleCustom = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;

  return (
    <DialogTitle className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          size="large">
          <Close />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
});

const CompleteActionModal = ({ closeModal, handleCompleteAction }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [messageContent, setMessageContent] = useState("")
  const [messageContentError, setMessageContentError] = useState(false);
  const [uploadSubmitError, setUploadSubmitError] = useState(false);
  const uploadedFiles = useSelector((state) => state.actions.completeActionFiles);

  const handleUpload = async (files) => {
    const uploadingFiles = await asyncMap(files, async (file) => {
      const fileOptimized = await resizeFile(file)
      return ({
        file: fileOptimized,
        id: uniqueId(),
        name: fileOptimized.name,
        readableSize: filesize(fileOptimized.size),
        preview: URL.createObjectURL(fileOptimized),
        fileType: fileOptimized.type,
        progress: 0,
        isNotUploaded: true,
        error: false,
        url: null
      });
    });

    dispatch(actions.addCompleteActionFile(uploadingFiles));
    uploadingFiles.forEach((file) => processUpload(file, updateFile, setUploadSubmitError));
  };

  const updateFile = (fileId, file) => {
    dispatch(actions.editCompleteActionFile(file, fileId));
  };

  const retryUpload = async (file) => {
    updateFile(file.id, {error: false})
    await processUpload(file, updateFile, setUploadSubmitError);
  }

  const handleDeleteFile = async (fileId) => {
    dispatch(actions.deleteCompleteActionFile(fileId));
  }

  const handleSave = async () => {
    let hasError = false;

    if (messageContent === '') {
      setMessageContentError(true);
      hasError = true;
    }

    const notUploaded = uploadedFiles.filter(file => file.error || file.isNotUploaded);
    if (notUploaded.length > 0) {
      setUploadSubmitError(true);
      hasError = true;
    }

    const filesIds = [];

    uploadedFiles.map(fl => {
      if (fl.id)
        return filesIds.push(fl.id);
      else if (fl._id)
        return filesIds.push(fl._id);
    });

    if (hasError) return;

    handleCompleteAction(filesIds, messageContent);
    closeModal();
  }

  return (
    <Dialog
      classes={{ paper: classes.paper}}
      style={styles.modalStyle}
      maxWidth="lg"
      fullWidth
      scroll='body'
      open
      onClose={closeModal}
    >
      <DialogTitleCustom id="customized-dialog-title-post-input" onClose={closeModal}>
        <div style={{ color: 'green', fontWeight: 'bold' }}>{i18n.t('handleCompleteActionFormActions.title')}</div>
      </DialogTitleCustom>
      <DialogContent dividers>
        <FormControl variant="standard" fullWidth>
          <Typography style={{ color: '#000' }}>{i18n.t('typographyCompleteActionModal.post')}</Typography>
          <TextField
            className={classes.root}
            value={messageContent}
            margin='normal'
            error={messageContentError}
            placeholder={i18n.t('inputViewNew.placeholder')}
            onChange={(e) => { setMessageContent(e.target.value); setMessageContentError(false); }}
            autoFocus
            multiline
            rows={4}
            variant='outlined'
            InputLabelProps={{
                shrink: true,
            }}
          />
          {messageContentError && <Typography variant='subtitle2' sx={styles.fileError}>{i18n.t('handleCompleteActionFormActions.inputError')}</Typography>}
        </FormControl>
        <div style={styles.sectionTitleContainer}>
          <AttachIcon style={{ color: '#000' }} />
          <Typography style={{ color: '#000' }}>{i18n.t('typographyCompleteActionModal.file')}</Typography>
        </div>
        <Upload onUpload={handleUpload} style={styles.uploadFileBox} />
        {!!uploadedFiles.length && (
          <FileList files={uploadedFiles} onDelete={handleDeleteFile} retry={(file) => retryUpload(file)}/>
        )}
        {uploadSubmitError && <div style={styles.fileError}>{i18n.t('divFormActions.fileError')}</div>}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSave} style={styles.sendButton}>
          {i18n.t('btnCompleteAction.text')}
        </Button>
      </DialogActions>    
    </Dialog>
  );
};

export default connect((state) => ({ auth: state.auth }))(CompleteActionModal);
